'use client'

import type { MeetingDay } from '@repo/core'
import type { ReactNode } from 'react'

import { HomeContactStepper } from '@/components/sections/home-contact-stepper.client'
import type { Dictionary } from '@/lib/home-copy'
import { BOOKING_ANCHOR, CONTAINER } from '@/lib/layout'
import { cn } from '@/lib/utils'

type Props = {
  copy: Dictionary['Public']['home']['contactStepper']
  /**
   * The "come see us" panel, passed in rather than imported.
   *
   * It reads the `city` rows to draw its map, and this file is a Client
   * Component only because the stepper is one — importing a server-only module
   * here would drag the Postgres driver into the browser bundle. A slot keeps
   * the stepper interactive and the map on the server.
   */
  office: ReactNode
  /** Free slots as the page was rendered. The picker refreshes them on mount. */
  meetingDays: readonly MeetingDay[]
}

/**
 * The contact page is the same on both audience branches — the stepper asks the
 * same questions of a creator and of a brand — so it takes no audience at all.
 * The surrounding chrome (which differs) comes from the layout.
 *
 * ── NO OPENING BAND AT ALL ───────────────────────────────────────────────
 * Every other page opens with `PageHeader`: a headline, a lead, the 3D mark
 * off to the right. This one did too, and the owner took all three out in two
 * passes — first the words, then the mark.
 *
 * The words went because the stepper's first screen asks exactly what the
 * headline announced ("Napisz albo pogadajmy") and says so in its own
 * labels: two headlines for one question. The mark went because what was left
 * was a 128px band containing one decorative object, above the form somebody
 * came here to fill in. Without it the first question is the first thing on
 * the screen, which is the whole point of the page.
 *
 * So there is no container band here any more, only enough top padding to
 * clear the fixed header — `PAGE_TOP` exists to open a band of content, and
 * with the content gone it was 128px of nothing.
 *
 * ── AND THE MARK IS GONE AGAIN, THIS TIME FOR ROOM ───────────────────────
 * It came back once — 223px of assembling dust on the page's own axis, the
 * only thing between the header and the first question — and the owner has
 * now taken it out again along with the two shortcut cards under it. The
 * reason is not that it was wrong; it is that this page has to be ONE SCREEN.
 * A form and a "where we are" panel do not fit under 350px of overture, and
 * the overture is the part nobody came for.
 *
 * ── ONE SCREEN ───────────────────────────────────────────────────────────
 * Owner's call: *„zakładka kontakt też musi być na zasadzie one page […]
 * podwyższamy formularz oraz siedziba i studio, wszystko ma być one page bez
 * scrolowania, rozmieść to ładnie"*. The form used to end at 1105px and the
 * office panel at 2026 — two screens of scrolling to reach an address.
 *
 * What made it fit was not shrinking either one: it was taking out the 350px
 * of overture above them and re-cutting the office panel into three columns
 * instead of two (see `office-location`), so it sits under the form as a strip
 * of facts rather than as a second page.
 *
 * The `h1` STAYS, as `sr-only`. A heading names the document — it is how a
 * screen reader's landmark list, and Google, know what this page is — and that
 * job does not depend on anybody seeing it. Removing it outright would leave
 * the route with no `h1` at all, which is a different change from removing a
 * headline nobody needed to read.
 */
export function KontaktPageView({ copy, office, meetingDays }: Props) {
  return (
    // No section background: the body's flat brand black carries every route,
    // so the product reads as one world.
    // The clearance is a `clamp()` against the window's HEIGHT, not a fixed
    // `pt-24`: on this page every pixel above the form is one the office panel
    // beside it does not get. The floor still clears the fixed header, which
    // measures 72px including its offset.
    <div className="relative w-full overflow-x-hidden">
      <div
        className={cn(
          CONTAINER,
          'pt-[clamp(4.75rem,8svh,7rem)] pb-[clamp(1rem,3svh,2.5rem)] lg:min-h-[100svh]',
        )}
      >
        <h1 className="sr-only">
          {copy.title} {copy.titleAccent}
        </h1>

        {/* The form, then where we are — in that order and both full width.
            Side by side was the first shape and it did not work: the office
            panel is a paragraph, an address, a timetable and a route list, and
            in a third of the window it ran to 1142px beside a 499px form.
            Under it, as a strip of three, it is 300-odd and the pair fits.
            `BOOKING_ANCHOR` names the form so the panel's "Umów spotkanie" has
            somewhere to go — the stepper handles its own `scroll-mt`, so the
            anchor lands clear of the fixed header. */}
        <div className="flex flex-col gap-[clamp(0.75rem,2svh,1.5rem)]">
          <HomeContactStepper copy={copy} id={BOOKING_ANCHOR} meetingDays={meetingDays} />
          {office}
        </div>
      </div>
    </div>
  )
}
